import { useContext } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import type { Appointment } from '@/react-query/useAppointments';

import { usePreferences } from '../../../../../react-query/usePreferences';
import type {
  TArchProcedureNames,
  TProcedureStatus,
  TVisitSummary,
} from '../../../../../react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '../../../../../utils/mixpanel';
import { useIsBasicOdontogram, useVisualizationProcedure } from '../hooks';
import { getRecommendedProcedures } from '../utils';

import { MouthArchIndicator } from './mouthArchIndicator';
import { QuadrantTxInfo } from './quadrantTxInfo';

export const MouthHover = ({
  activeImageId,
  appointment,
  visitSummary,
  selectedStatuses,
  module,
}: {
  activeImageId?: string;
  appointment?: Appointment;
  visitSummary?: TVisitSummary;
  selectedStatuses: TProcedureStatus[];
  module?: 'image-viewer' | 'patient-list';
}) => {
  const { data: prefs } = usePreferences();
  const { trackEvent } = useTrackEvent();
  const isBasicOdontogram = useIsBasicOdontogram();

  const { isIframe, externalPatientId, externalImageId, patientId } =
    useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const mandibular = visitSummary?.arches?.find(
    (arch) => arch.arch === 'mandibular',
  );
  const maxillary = visitSummary?.arches?.find(
    (arch) => arch.arch === 'maxillary',
  );

  const mouthProcedure = useVisualizationProcedure(
    visitSummary?.mouth?.procedures,
    selectedStatuses,
  );
  const maxillaryProcedure = useVisualizationProcedure(
    maxillary?.procedures,
    selectedStatuses,
  );
  const mandibularProcedure = useVisualizationProcedure(
    mandibular?.procedures,
    selectedStatuses,
  );
  const maxillaryRecommendedProcedures = getRecommendedProcedures(
    maxillary,
    prefs,
    isBasicOdontogram,
    visitSummary?.isPerioPatient,
  );
  const mandibularRecommendedProcedures = getRecommendedProcedures(
    mandibular,
    prefs,
    isBasicOdontogram,
    visitSummary?.isPerioPatient,
  );
  const fullMouthRecommendedProcedures = getRecommendedProcedures(
    visitSummary?.mouth,
    prefs,
    isBasicOdontogram,
    visitSummary?.isPerioPatient,
  );
  const isMaxillaryAlignerOpportunity =
    maxillaryProcedure?.name === 'aligner' &&
    maxillaryProcedure?.status === 'recommended';
  const isMandibularAlignerOpportunity =
    mandibularProcedure?.name === 'aligner' &&
    mandibularProcedure?.status === 'recommended';

  return (
    <>
      {Boolean(maxillaryProcedure) && (
        <HoverCard
          openDelay={100}
          closeDelay={100}
          onOpenChange={(open) => {
            if (open) {
              trackEvent(mixpanelEvents.odontogramHoverMouthPart, {
                mouthPart: 'maxillary',
                estimatedVisitId: visitSummary?.estimatedVisitId,
                unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
                status: maxillaryProcedure?.status,
                pmsPatientId: appointment?.patient.pmsPatientId,
                pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
                imagingPatientId: appointment?.patient.imagingPatientId,
                isIframe: !!isIframe,
                externalPatientId,
                externalImageId,
                imageId: activeImageId,
                patientId: selectedPatientId ?? patientId,
              });
            }
          }}
        >
          <HoverCardTrigger
            data-testid="maxillary-hover"
            className={classNames('vai-absolute vai-left-[-6px] vai-z-20', {
              'vai-top-[-10px]': isMaxillaryAlignerOpportunity,
              'vai-top-[-3px]': !isMaxillaryAlignerOpportunity,
            })}
          >
            <MouthArchIndicator
              module={module}
              visualizationName={
                maxillaryProcedure?.name as TArchProcedureNames
              }
              visualizationStatus={maxillaryProcedure?.status}
            />
          </HoverCardTrigger>
          {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
          <HoverCardPortal>
            <HoverCardContent
              className={classNames('vai-w-max vai-font-sans vai-z-[200]', {
                'vai-bg-white vai-border-slate-200 vai-text-slate-950':
                  module !== 'image-viewer',
                'vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100':
                  module === 'image-viewer',
              })}
            >
              <div className="vai-flex vai-flex-col vai-gap-4">
                <QuadrantTxInfo
                  module={module}
                  affectedArea={<Trans>Upper Arch</Trans>}
                  treatments={maxillary?.treatments}
                  procedures={maxillary?.procedures}
                  recommendedProcedures={maxillaryRecommendedProcedures}
                />
              </div>
            </HoverCardContent>
          </HoverCardPortal>
        </HoverCard>
      )}
      {Boolean(mandibularProcedure) && (
        <HoverCard
          openDelay={100}
          closeDelay={100}
          onOpenChange={(open) => {
            if (open) {
              trackEvent(mixpanelEvents.odontogramHoverMouthPart, {
                mouthPart: 'mandibular',
                estimatedVisitId: visitSummary?.estimatedVisitId,
                unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
                status: mandibularProcedure?.status,
                pmsPatientId: appointment?.patient.pmsPatientId,
                pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
                imagingPatientId: appointment?.patient.imagingPatientId,
                isIframe: !!isIframe,
                externalPatientId,
                externalImageId,
                imageId: activeImageId,
                patientId: selectedPatientId ?? patientId,
              });
            }
          }}
        >
          <HoverCardTrigger
            data-testid="mandibular-hover"
            className={classNames('vai-absolute vai-left-[-6px] vai-z-20', {
              'vai-bottom-[-10px]': isMandibularAlignerOpportunity,
              'vai-bottom-[-3px]': !isMandibularAlignerOpportunity,
            })}
          >
            <MouthArchIndicator
              module={module}
              visualizationName={
                mandibularProcedure?.name as TArchProcedureNames
              }
              visualizationStatus={mandibularProcedure?.status}
            />
          </HoverCardTrigger>
          {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
          <HoverCardPortal>
            <HoverCardContent
              className={classNames('vai-w-max vai-font-sans vai-z-[200]', {
                'vai-bg-white vai-border-slate-200 vai-text-slate-950':
                  module !== 'image-viewer',
                'vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100':
                  module === 'image-viewer',
              })}
            >
              <div className="vai-flex vai-flex-col vai-gap-4">
                <QuadrantTxInfo
                  module={module}
                  affectedArea={<Trans>Lower Arch</Trans>}
                  treatments={mandibular?.treatments}
                  procedures={mandibular?.procedures}
                  recommendedProcedures={mandibularRecommendedProcedures}
                />
              </div>
            </HoverCardContent>
          </HoverCardPortal>
        </HoverCard>
      )}
      {Boolean(mouthProcedure) && (
        <HoverCard
          openDelay={100}
          closeDelay={100}
          onOpenChange={(open) => {
            if (open) {
              trackEvent(mixpanelEvents.odontogramHoverMouthPart, {
                mouthPart: 'full',
                estimatedVisitId: visitSummary?.estimatedVisitId,
                unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
                status: mouthProcedure?.status,
                pmsPatientId: appointment?.patient.pmsPatientId,
                pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
                imagingPatientId: appointment?.patient.imagingPatientId,
                isIframe: !!isIframe,
                externalPatientId,
                externalImageId,
                imageId: activeImageId,
                patientId: selectedPatientId ?? patientId,
              });
            }
          }}
        >
          <HoverCardTrigger
            data-testid="full-mouth-hover"
            className="vai-absolute vai-top-1/2 vai-left-[-6px] -vai-translate-y-1/2 vai-z-10"
          >
            <MouthArchIndicator
              module={module}
              visualizationStatus={mouthProcedure?.status}
            />
          </HoverCardTrigger>
          {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
          <HoverCardPortal>
            <HoverCardContent
              className={classNames('vai-w-max vai-font-sans vai-z-[200]', {
                'vai-bg-white vai-border-slate-200 vai-text-slate-950':
                  module !== 'image-viewer',
                'vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100':
                  module === 'image-viewer',
              })}
            >
              <div className="vai-flex vai-flex-col vai-gap-4">
                <QuadrantTxInfo
                  module={module}
                  affectedArea={<Trans>Whole Dentition</Trans>}
                  treatments={visitSummary?.mouth?.treatments}
                  procedures={visitSummary?.mouth?.procedures}
                  recommendedProcedures={fullMouthRecommendedProcedures}
                />
              </div>
            </HoverCardContent>
          </HoverCardPortal>
        </HoverCard>
      )}
    </>
  );
};
